// apps/client/src/ui/screens/dialogue.tsx (M1-T19) — NPC dialogue panel (DESIGN.md speech bubble: pure white, 20px radius, 800 weight,
// soft shadow, rotated-square pointer; the speaker in the cream NPC/villager tag with the wood-600 paw). It types each line, a tap
// (or Space / Enter) completes the line, the next tap advances, and a tap after the last line closes it. M2-T11 reuses the panel
// (`DialoguePanel`, a controlled view: the caller owns the text and what a tap does) for villager talks.
import type { DialogueLine } from '@whiskerton/shared';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Bubble, Tag } from '../kit';
import type { ScreenDef } from '../router';

declare module '../router' { interface ScreenParams { dialogue: { speaker: string; lines: string[]; kind?: 'npc' | 'villager' } } }

/** Typewriter pace per character (presentation only). */
const CHAR_MS = 28;
// Bottom, centred in the gap between the Chat/Emotes column (left, ~10cqw) and the action pad (right, ~21.4cqw), so the panel
// never covers a HUD button.
const CSS = `
.dialogue { position: absolute; left: 44.4cqw; bottom: calc(var(--hud-inset) + env(safe-area-inset-bottom)); transform: translateX(-50%);
  width: min(62cqw, 760px); box-sizing: border-box; padding-top: 1.05em; cursor: pointer; outline: none; }
.dialogue .bubble { max-width: none; min-height: 4.2em; padding: 1.15em 1.5em 1.25em; font-size: clamp(16px, 2.15cqw, 24px); line-height: 1.4; }
.dialogue .bubble::after { left: 2.9em; bottom: auto; top: -6px; border-bottom-right-radius: 0; border-top-left-radius: 3px; }
.dialogue:focus-visible .bubble { box-shadow: var(--sh-soft), 0 0 0 3px var(--leaf-400); }
.dialogue-tag { position: absolute; top: 0; left: 1.4em; z-index: 1; font-size: clamp(14px, 1.8cqw, 19px); }
.dialogue-tag svg { width: 1em; height: 1em; color: var(--wood-600); flex: none; }
.dialogue-next { position: absolute; right: 1.5em; bottom: 1em; width: .62em; height: .62em; border-right: .2em solid var(--leaf-700);
  border-bottom: .2em solid var(--leaf-700); transform: rotate(45deg); animation: dialogue-next .7s var(--ease-out) infinite alternate; }
@keyframes dialogue-next { to { translate: 0 .22em; } }
.dialogue-choices { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: .6em; margin-top: .7em; cursor: default; }
.dialogue-choices .btn { font-size: clamp(14px, 1.8cqw, 18px); padding: .55em 1.1em .6em; }
@media (prefers-reduced-motion: reduce) { .dialogue-next { animation: none; } }
`;
let styled = false;
function injectStyle(): void {
  if (styled || typeof document === 'undefined') return;
  styled = true;
  const el = document.createElement('style');
  el.dataset.owner = 'dialogue';
  el.textContent = CSS;
  document.head.appendChild(el);
}
function Paw() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="currentColor">
      <ellipse cx="12" cy="16.2" rx="5.2" ry="4.4" /><ellipse cx="5.4" cy="10.6" rx="2.1" ry="2.6" /><ellipse cx="9.4" cy="6.4" rx="2.1" ry="2.7" />
      <ellipse cx="14.6" cy="6.4" rx="2.1" ry="2.7" /><ellipse cx="18.6" cy="10.6" rx="2.1" ry="2.6" />
    </svg>
  );
}

export interface DialoguePanelProps {
  speaker: string; speakerKind: 'npc' | 'villager'; text: string; mood: DialogueLine['mood']; onNext(): void; children?: ReactNode;
  /** The "tap to continue" chevron; by default shown whenever there are no choices. */
  next?: boolean;
}
/** The speech panel: the speaker in the cream tag with the paw, the line in a white bubble, optional choice buttons under it. A tap
 *  (or Space / Enter on the panel itself) calls `onNext`; taps and keys on the choices stay with the choices. */
export function DialoguePanel(p: DialoguePanelProps) {
  injectStyle();
  const root = useRef<HTMLDivElement>(null);
  // Takes the keyboard (Space / Enter advance) unless something else already has it, such as the chat field.
  useEffect(() => {
    if (!document.activeElement || document.activeElement === document.body) root.current?.focus({ preventScroll: true });
  }, []);
  return (
    <div ref={root} className="hit dialogue" data-testid="dialogue" data-kind={p.speakerKind} data-mood={p.mood} role="button" tabIndex={0} onClick={p.onNext}
      onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === ' ' || e.key === 'Enter')) { e.preventDefault(); e.stopPropagation(); p.onNext(); } }}>
      <span className="dialogue-tag"><Tag color="villager"><Paw />{p.speaker}</Tag></span>
      <Bubble>{p.text}</Bubble>
      {p.children && <div className="dialogue-choices" onClick={(e) => e.stopPropagation()}>{p.children}</div>}
      {(p.next ?? !p.children) && <span className="dialogue-next" aria-hidden />}
    </div>
  );
}

function Dialogue({ params, close }: { params: { speaker: string; lines: string[]; kind?: 'npc' | 'villager' }; close(): void }) {
  const [line, setLine] = useState(0), [shown, setShown] = useState(0);
  const text = params.lines[line] ?? '';
  const done = shown >= text.length;
  // One interval per line (not a timeout per character), so a batch of elapsed ticks lands in one render.
  useEffect(() => {
    if (done) return;
    const id = setInterval(() => setShown((n) => n + 1), CHAR_MS);
    return () => clearInterval(id);
  }, [text, done]);
  const tap = () => {
    if (!done) { setShown(text.length); return; }
    if (line + 1 < params.lines.length) { setLine(line + 1); setShown(0); return; }
    close();
  };
  return <DialoguePanel speaker={params.speaker} speakerKind={params.kind ?? 'npc'} text={text.slice(0, shown)} mood="neutral" onNext={tap} next={done} />;
}
export const screen: ScreenDef<'dialogue'> = { id: 'dialogue', layer: 'overlay', component: ({ params, close }) => <Dialogue params={params} close={close} /> };
